import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { SERVICE_OPTIONS } from '@/lib/validations/provider';
import { Loader2, Briefcase, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

interface EditServicesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentServices: string[];
  providerType: 'veterinarian_individual' | 'veterinary_clinic' | 'pet_store' | 'dog_walker' | 'grooming_spa' | 'daycare_hotel' | 'school_training';
  onUpdate: (services: string[]) => Promise<void>;
}

export const EditServicesModal = ({
  open,
  onOpenChange,
  currentServices,
  providerType,
  onUpdate,
}: EditServicesModalProps) => {
  const [selectedServices, setSelectedServices] = useState<string[]>(currentServices);
  const [customService, setCustomService] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedServices(currentServices);
      setCustomService('');
    }
  }, [open, currentServices]);

  const availableServices = SERVICE_OPTIONS[providerType] || [];

  const handleToggleService = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  const handleAddCustomService = () => {
    const trimmed = customService.trim();
    if (!trimmed) {
      toast.error('Please enter a service name');
      return;
    }
    if (selectedServices.includes(trimmed)) {
      toast.error('This service is already added');
      return;
    }
    if (selectedServices.length >= 10) {
      toast.error('Maximum 10 services allowed');
      return;
    }
    setSelectedServices([...selectedServices, trimmed]);
    setCustomService('');
  };

  const handleRemoveService = (service: string) => {
    setSelectedServices((prev) => prev.filter((s) => s !== service));
  };

  const handleSave = async () => {
    if (selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    if (selectedServices.length > 10) {
      toast.error('Maximum 10 services allowed');
      return;
    }

    setIsLoading(true);
    try {
      await onUpdate(selectedServices);
      toast.success('Services updated successfully');
      onOpenChange(false);
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update services');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" />
            Edit Services Offered
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Selected Services Display */}
          <div className="space-y-2">
            <Label>Selected Services ({selectedServices.length}/10)</Label>
            <div className="flex flex-wrap gap-2 min-h-[50px] p-3 border rounded-md bg-muted/20">
              {selectedServices.length === 0 ? (
                <span className="text-sm text-muted-foreground">No services selected</span>
              ) : (
                selectedServices.map((service) => (
                  <Badge
                    key={service}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {service}
                    <button
                      type="button"
                      onClick={() => handleRemoveService(service)}
                      className="ml-1 hover:bg-background/20 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))
              )}
            </div>
          </div>

          {/* Suggested Services */}
          <div className="space-y-2">
            <Label>Common Services</Label>
            <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-3">
              {availableServices.map((service) => (
                <div key={service} className="flex items-center space-x-2">
                  <Checkbox
                    id={service}
                    checked={selectedServices.includes(service)}
                    onCheckedChange={() => handleToggleService(service)}
                  />
                  <label
                    htmlFor={service}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {service}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Service Input */}
          <div className="space-y-2">
            <Label>Add Custom Service</Label>
            <div className="flex gap-2">
              <Input
                value={customService}
                onChange={(e) => setCustomService(e.target.value)}
                placeholder="Enter custom service name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomService();
                  }
                }}
                maxLength={50}
              />
              <Button
                type="button"
                onClick={handleAddCustomService}
                variant="outline"
                size="icon"
                disabled={selectedServices.length >= 10}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Services
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
